import { Event } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import {
  AnalyticsReportEmployeeScope,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useAbsencesReportData,
} from '~/data/reports';
import {
  ReportColumnsVisibility,
  ReportDataExport,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportViewLayout,
  useReportDataset,
} from '~/scenes/PortalModules/Analytics/common';

import { useAbsencesReport } from './useAbsencesReport';

export const AbsencesReport = () => {
  const { t } = useTranslation();

  const apiRef = useGridApiRef();

  const loggedInEmployeeId = useLoggedInEmployeeId();

  const defaultReportSelection = {
    reportType: {
      type: AnalyticsReportTypes.Team,
      role: AnalyticsReportRoleScope.AltManager,
      employeeId: loggedInEmployeeId,
      scope: AnalyticsReportScope.Direct,
    },
    params: {
      startDate: moment().subtract(1, 'month').toDate(),
      endDate: moment().add(2, 'months').toDate(),
      empScope: AnalyticsReportEmployeeScope.AllEmployees,
    },
  };

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
        roles: [
          AnalyticsReportRoleScope.Manager,
          AnalyticsReportRoleScope.DeputyManager,
        ],
      },
    },
  });

  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = useAbsencesReportData(reportDatasetProps.reportSelection);

  const filteredData = useMemo(() => {
    return data.filter(
      (employee) =>
        employee.fEmpAbsenceShortHistory.length > 0 ||
        employee.fEmpAbsenceLongHistory.length > 0,
    );
  }, [data]);

  const { table } = useAbsencesReport({
    data: filteredData,
  });

  return (
    <ReportViewLayout
      heading={t('laAbsencesReport')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout showHeading={true}>
              <ReportDatasetSelector
                {...reportDatasetProps}
                isLoading={isLoadingReportData}
                failedToLoad={failedToLoad}
                renderCurrentParams={({ params }) => {
                  return (
                    <>{`${moment(params?.startDate).format('L')} - ${moment(
                      params?.endDate,
                    ).format('L')}`}</>
                  );
                }}
                renderParamsFields={({ params, onChange }) => {
                  return (
                    <ReportDateRangeSelector
                      startDate={params.startDate}
                      endDate={params.endDate}
                      onRangeChange={(startDate, endDate) =>
                        onChange({
                          ...params,
                          startDate: startDate ?? params.startDate,
                          endDate: endDate ?? params.endDate,
                        })
                      }
                      mode={ReportDateRangeSelectorMode.DateRange}
                    />
                  );
                }}
              />
              <ReportDatasetRowCount
                count={table.rows.length}
                description={t('laAbsences')}
                icon={<Event />}
              />
            </ReportDatasetLayout>
          ),
          actions: (
            <>
              <ReportColumnsVisibility
                apiRef={apiRef}
                columnGroupingModel={table.columnGroupingModel}
                isLoading={isLoadingReportData}
              />
              <ReportDataExport
                title={t('laAbsencesReport')}
                startDate={reportDatasetProps.reportSelection.params?.startDate}
                endDate={reportDatasetProps.reportSelection.params?.endDate}
                apiRef={apiRef}
                isLoading={isLoadingReportData}
              />
            </>
          ),
        },
      }}
    >
      <SolaForceDataGrid
        apiRef={apiRef}
        columns={table.columnConfigs}
        columnGroupingModel={table.columnGroupingModel}
        experimentalFeatures={{ columnGrouping: true }}
        rows={table.rows}
        density={'compact'}
        getRowId={(row) => row.rowId}
        pinnedColumns={{
          left: ['employeeId', 'firstName', 'lastName'],
        }}
      />
    </ReportViewLayout>
  );
};
